import { useState, useEffect, useRef } from 'react';
import {
  Mail, Phone, MapPin, Instagram, Facebook, Twitter, Youtube,
  Linkedin, Music, Calendar, ExternalLink, Play, ChevronRight, ChevronLeft, Star, Quote,
  User, ArrowRight, X, Plus, Briefcase, Users, HelpCircle, MessageSquare, CheckCircle,
  Pause
} from 'lucide-react';

import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { AnimatedSection } from './AnimatedSection';

const THEMES = {
  dark_stage: {
    bg: 'bg-slate-900',
    text: 'text-white',
    textMuted: 'text-slate-400',
    card: 'bg-slate-800',
    border: 'border-slate-700',
    accent: 'text-orange-500',
    button: 'bg-orange-500 hover:bg-orange-400',
  },
  theatre_red: {
    bg: 'bg-[#1a0c0e] tx-velvet',
    text: 'text-[#f8f0e6]',
    textMuted: 'text-[#bfa39a]',
    card: 'bg-[#120709] tx-velvet-alt',
    border: 'border-[#3b2125]',
    accent: 'text-[#d4af37]',
    button: 'bg-[#b91c1c] hover:bg-[#dc2626]',
  },
  ivory_gold: {
    bg: 'bg-[#faf7f1]',
    text: 'text-[#1c1710]',
    textMuted: 'text-[#8d7f68]',
    card: 'bg-[#f1ebdd]',
    border: 'border-[#e2d8c3]',
    accent: 'text-[#b08d2f]',
    button: 'bg-[#1c1710] hover:bg-[#3a2f1e]',
  },
  light_modern: {
    bg: 'bg-white',
    text: 'text-slate-900',
    textMuted: 'text-slate-500',
    card: 'bg-slate-50',
    border: 'border-slate-200',
    accent: 'text-blue-600',
    button: 'bg-blue-600 hover:bg-blue-500',
  },
  colorful_kids: {
    bg: 'bg-amber-50',
    text: 'text-purple-900',
    textMuted: 'text-purple-600',
    card: 'bg-white',
    border: 'border-pink-200',
    accent: 'text-pink-500',
    button: 'bg-pink-500 hover:bg-pink-400',
  },
  elegant_classic: {
    bg: 'bg-stone-900',
    text: 'text-stone-50',
    textMuted: 'text-stone-400',
    card: 'bg-stone-800',
    border: 'border-stone-700',
    accent: 'text-amber-400',
    button: 'bg-amber-500 hover:bg-amber-400',
  },
  bold_artist: {
    bg: 'bg-zinc-900',
    text: 'text-white',
    textMuted: 'text-zinc-400',
    card: 'bg-zinc-800',
    border: 'border-zinc-700',
    accent: 'text-red-500',
    button: 'bg-red-500 hover:bg-red-400',
  },
};

// Social icon mapper
const getSocialIcon = (platform) => {
  const name = platform.toLowerCase();
  if (name.includes('instagram')) return Instagram;
  if (name.includes('facebook')) return Facebook;
  if (name.includes('twitter') || name.includes('x')) return Twitter;
  if (name.includes('youtube')) return Youtube;
  if (name.includes('linkedin')) return Linkedin;
  if (name.includes('spotify') || name.includes('music')) return Music;
  return ExternalLink;
};


export const GallerySection = ({ data, theme, primaryColor }) => {
  const t = THEMES[theme] || THEMES.dark_stage;
  const images = data?.images || [];
  const layout = data?.layout || 'grid';
  const spacing = data?.spacing || 'spaced';
  const columns = data?.columns || 3;
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [carouselIndex, setCarouselIndex] = useState(0);
  
  if (images.length === 0) return null;

  const gapClass = spacing === 'flush' ? 'gap-0' : 'gap-4';
  const roundedClass = spacing === 'flush' ? '' : 'rounded-xl';

  const openLightbox = (idx) => {
    setLightboxIndex(idx);
    setLightboxOpen(true);
  };

  // Grid Layout - uniform squares
  const renderGridLayout = () => (
    <div className={cn(
      "grid",
      columns === 2 && "grid-cols-2 md:grid-cols-2",
      columns === 3 && "grid-cols-2 md:grid-cols-3",
      columns === 4 && "grid-cols-2 md:grid-cols-4",
      gapClass
    )}>
      {images.map((img, idx) => (
        <AnimatedSection 
          key={idx} 
          animation="scale" 
          delay={idx * 80}
          className={cn(
            "group relative overflow-hidden cursor-pointer aspect-square",
            roundedClass
          )}
          onClick={() => openLightbox(idx)}
        >
          <img 
            src={img.url} 
            alt={img.caption || `Gallery ${idx + 1}`} 
            className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-end justify-center pb-4">
            <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-sm font-medium px-3 py-1 bg-black/50 rounded-full">
              {img.caption || 'View'}
            </span>
          </div>
        </AnimatedSection>
      ))}
    </div>
  );

  // Masonry Layout - varied heights with featured first
  const renderMasonryLayout = () => (
    <div className={cn("grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 auto-rows-[200px]", gapClass)}>
      {images.map((img, idx) => {
        // Create varied sizes - first is large, then alternate between tall and wide
        const isFeatured = idx === 0;
        const isTall = idx % 5 === 1 || idx % 5 === 4;
        const isWide = idx % 5 === 3;
        
        return (
          <AnimatedSection 
            key={idx} 
            animation="scale" 
            delay={idx * 80}
            className={cn(
              "group relative overflow-hidden cursor-pointer",
              roundedClass,
              isFeatured && "col-span-2 row-span-2",
              isTall && !isFeatured && "row-span-2",
              isWide && !isFeatured && "col-span-2"
            )}
            onClick={() => openLightbox(idx)}
          >
            <img 
              src={img.url} 
              alt={img.caption || `Gallery ${idx + 1}`} 
              className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-end justify-center pb-4">
              <span className="text-white text-sm font-medium px-3 py-1 bg-black/50 rounded-full backdrop-blur-sm">
                {img.caption || 'View'}
              </span>
            </div>
          </AnimatedSection>
        );
      })}
    </div>
  );

  // Carousel Layout - horizontal slider
  const renderCarouselLayout = () => (
    <div className="relative">
      {/* Main carousel */}
      <div className="relative overflow-hidden rounded-2xl">
        <div 
          className="flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${carouselIndex * 100}%)` }}
        >
          {images.map((img, idx) => (
            <div key={idx} className="min-w-full aspect-[16/9] relative">
              <img 
                src={img.url} 
                alt={img.caption || `Gallery ${idx + 1}`} 
                className="w-full h-full object-cover cursor-pointer"
                onClick={() => openLightbox(idx)}
              />
              {img.caption && (
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                  <p className="text-white text-lg font-medium">{img.caption}</p>
                </div>
              )}
            </div>
          ))}
        </div>
        
        {/* Navigation arrows */}
        {images.length > 1 && (
          <>
            <button 
              onClick={() => setCarouselIndex(Math.max(0, carouselIndex - 1))}
              className={cn(
                "absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center transition-all",
                "bg-black/50 hover:bg-black/70 text-white backdrop-blur-sm",
                carouselIndex === 0 && "opacity-50 cursor-not-allowed"
              )}
              disabled={carouselIndex === 0}
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button 
              onClick={() => setCarouselIndex(Math.min(images.length - 1, carouselIndex + 1))}
              className={cn(
                "absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center transition-all",
                "bg-black/50 hover:bg-black/70 text-white backdrop-blur-sm",
                carouselIndex === images.length - 1 && "opacity-50 cursor-not-allowed"
              )}
              disabled={carouselIndex === images.length - 1}
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
      </div>
      
      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="flex gap-2 mt-4 overflow-x-auto pb-2 justify-center">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setCarouselIndex(idx)}
              className={cn(
                "w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all",
                carouselIndex === idx 
                  ? "border-orange-500 opacity-100" 
                  : "border-transparent opacity-60 hover:opacity-100"
              )}
            >
              <img src={img.url} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // Lightbox Modal
  const renderLightbox = () => {
    if (!lightboxOpen) return null;
    
    return (
      <div 
        className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
        onClick={() => setLightboxOpen(false)}
      >
        {/* Close button */}
        <button 
          className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
          onClick={() => setLightboxOpen(false)}
        >
          <X className="w-6 h-6" />
        </button>
        
        {/* Navigation */}
        {images.length > 1 && (
          <>
            <button 
              className="absolute left-4 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(Math.max(0, lightboxIndex - 1)); }}
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
            <button 
              className="absolute right-4 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(Math.min(images.length - 1, lightboxIndex + 1)); }}
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          </>
        )}
        
        {/* Image */}
        <div className="max-w-[90vw] max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
          <img 
            src={images[lightboxIndex]?.url} 
            alt={images[lightboxIndex]?.caption || ''} 
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
          />
          {images[lightboxIndex]?.caption && (
            <p className="text-white text-center mt-4 text-lg">{images[lightboxIndex].caption}</p>
          )}
        </div>
        
        {/* Counter */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-sm">
          {lightboxIndex + 1} / {images.length}
        </div>
      </div>
    );
  };

  return (
    <section id="gallery" className={cn("py-32 px-4 overflow-hidden", t.card)}>
      <div className="max-w-7xl mx-auto">
        <AnimatedSection animation="fadeUp" className="text-center mb-16">
          <span className="text-sm uppercase tracking-widest font-semibold mb-4 block" style={{ color: primaryColor }}>
            Visual Portfolio
          </span>
          <h2 className={cn("text-4xl md:text-5xl font-bold", t.text)} style={{ fontFamily: "'Outfit', sans-serif" }}>
            {data.title || 'Gallery'}
          </h2>
        </AnimatedSection>
        
        {layout === 'grid' && renderGridLayout()}
        {layout === 'masonry' && renderMasonryLayout()}
        {layout === 'carousel' && renderCarouselLayout()}
      </div>
      
      {renderLightbox()}
    </section>
  );
};


